import React from 'react';
import type { TFunction } from 'i18next';

export type CompositeStatusKind = 'positive' | 'negative' | 'neutral';

/** Classify static composite status cell values (Open, Enabled, Disabled, etc.). */
export function classifyCompositeStatus(raw: unknown): CompositeStatusKind {
  const v = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!v || v === '—') return 'neutral';
  if (/\b(open|enabled|yes|active|up|running|ok)\b/.test(v) || v === 'on') {
    return 'positive';
  }
  if (/\b(closed|disabled|no|inactive|down|off|failed)\b/.test(v)) {
    return 'negative';
  }
  return 'neutral';
}

export interface CompositeStatusIconTooltips {
  positive: string;
  negative: string;
  /** Defaults to rtuDetail.staticData.shared.statusIcon.neutral */
  neutral?: string;
}

const DEFAULT_NEUTRAL_KEY = 'rtuDetail.staticData.shared.statusIcon.neutral';

export function CompositeStatusIconCell({
  raw,
  tooltips,
  t,
}: {
  raw: unknown;
  tooltips: CompositeStatusIconTooltips;
  t: TFunction;
}) {
  const kind = classifyCompositeStatus(raw);
  const display = String(raw ?? '—').trim() || '—';
  const neutralKey = tooltips.neutral ?? DEFAULT_NEUTRAL_KEY;
  const messageKey = kind === 'positive' ? tooltips.positive : kind === 'negative' ? tooltips.negative : neutralKey;
  const tooltip = t(messageKey, { value: display });

  const isPos = kind === 'positive';
  const isNeg = kind === 'negative';
  const color = isPos ? 'green' : isNeg ? 'red' : '#64748b';
  const icon = isPos ? 'pi-circle-on' : 'pi-circle-fill';

  return (
    <span role="img" aria-label={tooltip} title={tooltip} className="rtu-status-icon-cell">
      <i className={`pi ${icon}`} style={{ color, fontSize: '0.7rem' }} aria-hidden />
    </span>
  );
}
